"use client";

import React, { useState, useEffect, useRef } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { Hash, List, ChevronRight, Menu } from "lucide-react";
import { cn } from "@/lib/utils";

interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  className?: string;
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({
  content,
  className = "",
}) => {
  const [items, setItems] = useState<TableOfContentsItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headings: TableOfContentsItem[] = [];
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    let match;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");

      headings.push({ id, text, level });
    }

    setItems(headings);
  }, [content]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-15% 0% -70% 0%", threshold: 0 }
    );

    document
      .querySelectorAll("h2, h3")
      .forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav
      className={cn("hidden lg:block space-y-8", className)}
      ref={containerRef}
    >
      <div className="flex items-center gap-3 pb-4 border-b border-border/50">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          <List className="w-4 h-4" />
        </div>
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-foreground">
          Table of Contents
        </h3>
      </div>

      <div className="relative">
        {/* Active Line Indicator */}
        <div className="absolute left-0 top-0 w-[2px] h-full bg-border/20 rounded-full" />

        <ul className="space-y-1 relative">
          {items.map((item) => (
            <li key={item.id} className="relative">
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById(item.id);
                  if (element) {
                    const yOffset = -100;
                    const y =
                      element.getBoundingClientRect().top +
                      window.pageYOffset +
                      yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }}
                className={cn(
                  "group flex items-center py-2.5 text-sm transition-all duration-300 relative pl-6",
                  activeId === item.id
                    ? "text-primary font-bold"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {/* Active Line Marker */}
                {activeId === item.id && (
                  <motion.div
                    layoutId="active-toc-line"
                    className="absolute left-0 w-[2px] h-full bg-primary z-10"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}

                <span
                  className={cn(
                    "flex-1 truncate",
                    item.level === 3
                      ? "pl-4 text-[13px] opacity-80"
                      : "font-bold"
                  )}
                >
                  {item.text}
                </span>

                <ChevronRight
                  className={cn(
                    "w-3 h-3 transition-all duration-300",
                    activeId === item.id
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 -translate-x-2"
                  )}
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default TableOfContents;
